import {
    getClauseTypes,
    getPolicyTypes,
    IClause,
    IClauseUpdate,
    updateClauseById,
} from "@/api";
import { Button } from "@/components/shared/buttons/Button";
import { Panel } from "@/components/shared/Panel";
import {
    CLAUSE_TYPE_QUERY_FILTER,
    POLICY_TYPE_QUERY_FILTER,
} from "@/config/constants";
import { Edit } from "@mui/icons-material";
import { Chip, Stack, Typography } from "@mui/material";
import {
    FormAutoCompleteElement,
    FormContainer,
    FormTextFieldElement,
} from "@rhf-kit/mui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

interface IProps {
    clause: IClause;
}

interface IClauseInfoForm {
    policy_type_id: number;
    clause_type_ids: number[];
    title: string;
    description: string;
    guidance: string;
}

/**
 * Section displaying clause information with inline editing
 */
export const ClauseInfoSection = ({ clause }: IProps) => {
    const [isEditing, setIsEditing] = useState(false);
    const queryClient = useQueryClient();

    const { data: policyTypes, isPending: isPolicyTypesLoading } = useQuery({
        queryKey: ["policyTypes"],
        queryFn: () => getPolicyTypes(1, 100, POLICY_TYPE_QUERY_FILTER),
        enabled: isEditing,
    });

    const { data: clauseTypes, isPending: isClauseTypesLoading } = useQuery({
        queryKey: ["clauseTypes"],
        queryFn: () => getClauseTypes(1, 100, CLAUSE_TYPE_QUERY_FILTER),
        enabled: isEditing,
    });

    const { mutate: updateClause, isPending: isSaving } = useMutation({
        mutationFn: async (formData: IClauseInfoForm) => {
            const clauseUpdate: IClauseUpdate = {
                title: formData.title,
                description: formData.description,
                guidance: formData.guidance,
                policy_type_id: formData.policy_type_id,
                clause_type_ids: formData.clause_type_ids,
            };
            return updateClauseById(clause.id, clauseUpdate);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["admin-clause-details", String(clause.id)],
            });
            queryClient.invalidateQueries({ queryKey: ["admin-clauses"] });
            setIsEditing(false);
        },
    });

    const handleSave = (formData: IClauseInfoForm) => {
        updateClause(formData);
    };

    const handleCancel = () => {
        setIsEditing(false);
    };

    const defaultValues: IClauseInfoForm = {
        policy_type_id: clause.policy_type?.id ?? -1,
        clause_type_ids: clause.clause_types?.map(({ id }) => id) ?? [],
        title: clause.title.trim(),
        description: clause.description,
        guidance: clause.guidance,
    };

    return (
        <Panel
            title="Clause Information"
            actions={
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Edit fontSize="small" />}
                    onClick={() => setIsEditing(true)}
                    sx={{
                        visibility: isEditing ? "hidden" : "visible",
                        color: (theme) => theme.palette.primary.contrastText,
                        borderColor: (theme) =>
                            theme.palette.primary.contrastText,
                        "&:hover": {
                            backgroundColor: (theme) =>
                                theme.palette.action.hover,
                        },
                        "& .MuiSvgIcon-root": {
                            fontSize: "1rem",
                        },
                    }}
                >
                    Edit
                </Button>
            }
        >
            {isEditing ? (
                <FormContainer
                    defaultValues={defaultValues}
                    onSuccess={handleSave}
                >
                    <Stack spacing={2.5}>
                        <FormAutoCompleteElement
                            label="Policy Type"
                            name="policy_type_id"
                            loading={isPolicyTypesLoading}
                            options={policyTypes?.items ?? []}
                            getOptionLabel={(option) =>
                                `${option.name} - ${option.short_name}`
                            }
                            size="small"
                            matchId
                            fullWidth
                            required
                        />

                        <FormAutoCompleteElement
                            label="Clause Types"
                            name="clause_type_ids"
                            options={clauseTypes?.items ?? []}
                            getOptionLabel={(option) => {
                                if (!option) return "";
                                return option.name;
                            }}
                            loading={isClauseTypesLoading}
                            size="small"
                            matchId
                            fullWidth
                            multiple
                            required
                        />

                        <FormTextFieldElement
                            label="Clause Title"
                            name="title"
                            size="small"
                            rules={{
                                validate: (value: string) =>
                                    value.trim() !== "" ||
                                    "Clause Title cannot be empty",
                            }}
                            fullWidth
                            required
                        />

                        <FormTextFieldElement
                            label="Description"
                            name="description"
                            size="small"
                            fullWidth
                            required
                        />

                        <FormTextFieldElement
                            label="Guidance"
                            name="guidance"
                            size="small"
                            multiline
                            minRows={3}
                            fullWidth
                            required
                        />

                        <Stack
                            direction="row"
                            gap={1}
                            justifyContent="flex-end"
                        >
                            <Button
                                type="button"
                                variant="outlined"
                                size="small"
                                onClick={handleCancel}
                                disabled={isSaving}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                size="small"
                                variant="contained"
                                loading={isSaving}
                            >
                                Save
                            </Button>
                        </Stack>
                    </Stack>
                </FormContainer>
            ) : (
                <Stack spacing={2}>
                    <InfoField label="Title" value={clause.title} />
                    <InfoField
                        label="Policy Type"
                        value={
                            clause.policy_type
                                ? `${clause.policy_type.name} - ${clause.policy_type.short_name}`
                                : "Not assigned"
                        }
                    />
                    <InfoField
                        label="Clause Types"
                        value={
                            clause.clause_types?.length > 0 ? (
                                <Stack direction="row" gap={1} flexWrap="wrap">
                                    {clause.clause_types.map((ct) => (
                                        <Chip
                                            key={ct.id}
                                            label={ct.name}
                                            size="small"
                                            variant="outlined"
                                        />
                                    ))}
                                </Stack>
                            ) : (
                                "Not assigned"
                            )
                        }
                    />
                    <InfoField label="Description" value={clause.description} />
                    <InfoField
                        label="Guidance"
                        value={clause.guidance}
                        multiline
                    />
                </Stack>
            )}
        </Panel>
    );
};

interface IInfoFieldProps {
    label: string;
    value: React.ReactNode;
    multiline?: boolean;
}

const InfoField = ({ label, value, multiline }: IInfoFieldProps) => (
    <Stack spacing={0.5}>
        <Typography variant="body2" color="textSecondary" fontWeight={500}>
            {label}
        </Typography>
        {typeof value === "string" ? (
            <Typography
                variant="body2"
                sx={{
                    whiteSpace: multiline ? "pre-wrap" : "normal",
                }}
            >
                {value || "-"}
            </Typography>
        ) : (
            value
        )}
    </Stack>
);
